import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useProject, useProjectDispatch } from '../../store/projectContext';
import { PROJECT_ACTIONS } from '../../store/projectReducer';
import { useCurrentFrame } from '../use-current-frame';
import { getActiveSubtitle } from './subtitleUtils';

const Overlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding-bottom: 4%;
  z-index: 10;
  overflow: hidden;
  pointer-events: none;
  /* No horizontal padding here — the 5% side margin is handled by SubBox's
     max-width so the container-type inline-size measures the full frame width.
     padding-bottom: 4% matches the preview's SubtitleOverlay positioning. */
  container-type: inline-size;
`;

const SubBox = styled.div`
  max-width: 90%;
  text-align: center;
  line-height: 1.4;
  cursor: text;
  min-width: 80px;
  border: 2px solid transparent;
  transition: border-color 0.15s;
  font-weight: bold;
  word-wrap: break-word;

  &:hover { border-color: rgba(102, 126, 234, 0.5); }
  &:focus-within { border-color: #667eea; }
`;

const WordSpan = styled.span`
  transition: opacity 0.05s, color 0.1s;
`;

const WordRevealRenderer = ({ words, timeSec, highlightColor }) => {
  if (!words || words.length === 0) return null;

  return (
    <>
      {words.map((word, i) => {
        const isRevealed = timeSec >= word.startTime;
        const isActive = timeSec >= word.startTime && timeSec < word.endTime;

        return (
          <WordSpan
            key={i}
            style={{
              opacity: isRevealed ? 1 : 0.3,
              color: isActive ? (highlightColor || '#ffd700') : undefined,
            }}
          >
            {word.text}
          </WordSpan>
        );
      })}
    </>
  );
};

const SubtitleOverlay = () => {
  const project = useProject();
  const dispatch = useProjectDispatch();
  const frame = useCurrentFrame();
  const fps = project?.fps || 30;
  const timeSec = frame / fps;
  const textRef = useRef(null);
  const [editing, setEditing] = useState(false);

  const active = getActiveSubtitle(project?.subtitles, timeSec);
  // Use the original authored canvas width as the denominator for the cqw formula.
  // The render server overwrites project.width to the output resolution (e.g. 1920)
  // but stores the original in project._authoredWidth. Using the authoring width keeps
  // the font-size proportion correct: (24px authored on 800px) → 3% of any canvas.
  const projectWidth = project?._authoredWidth || project?.width || 800;

  useEffect(() => {
    if (textRef.current && active && !editing) {
      textRef.current.textContent = active.text;
    }
  }, [active?.id, active?.text, editing]);

  if (!active) return null;

  const s = active.style || {};
  const hasWords = active.words && active.words.length > 0 && !editing;
  // Scale padding proportionally with the authored canvas width,
  // matching the preview's cqw approach so the subtitle box looks the same at any render resolution.
  const isVertical = (project?.height || 450) > (project?.width || 800);
  const pxToCqw = (px) => `${(px / projectWidth) * 100}cqw`;
  const padV = isVertical ? 6 : 8;
  const padH = isVertical ? 12 : 20;

  const handleEdit = () => {
    setEditing(true);
  };

  const handleBlur = () => {
    setEditing(false);
    if (!textRef.current) return;
    const newText = textRef.current.textContent || '';
    if (newText !== active.text) {
      // Clear words when user manually edits text — falls back to plain display
      dispatch({
        type: PROJECT_ACTIONS.UPDATE_SUBTITLE,
        payload: { id: active.id, updates: { text: newText, words: [] } },
      });
    }
  };

  return (
    <Overlay>
      <SubBox
        onClick={handleEdit}
        style={{
          fontFamily: s.fontFamily || 'Inter, sans-serif',
          // fontSize is authored in project-space px (e.g. 24 on an 800px
          // canvas). Express it as cqw so it scales with the composition's
          // actual width at any export resolution, matching how
          // TextOverlayRenderer scales overlay text.
          fontSize: `${((s.fontSize || 24) / projectWidth) * 100}cqw`,
          color: s.color || '#ffffff',
          background: s.background || 'rgba(0,0,0,0.7)',
          borderRadius: s.borderRadius || 6,
          padding: `${pxToCqw(padV)} ${pxToCqw(padH)}`,
          textShadow: s.textShadow || 'none',
          letterSpacing: s.letterSpacing || 0,
          textTransform: s.textTransform || 'none',
        }}
      >
        {hasWords ? (
          <WordRevealRenderer
            words={active.words}
            timeSec={timeSec}
            highlightColor={s.highlightColor || '#ffd700'}
          />
        ) : (
          <div
            ref={textRef}
            contentEditable
            suppressContentEditableWarning
            style={{ outline: 'none', whiteSpace: 'pre-wrap', minHeight: '1.2em' }}
            onFocus={() => setEditing(true)}
            onBlur={handleBlur}
          >
            {active.text}
          </div>
        )}
      </SubBox>
    </Overlay>
  );
};

export default SubtitleOverlay;
